class CartItems extends HTMLElement {
  constructor() {
    super();
    this.cartDrawer = document.querySelector('cart-drawer');
    this.cartPage = this.closest('cart-page');
    this._debouncedUpdaters = new Map();
  }

  _getDebouncedUpdate(key) {
    if (!this._debouncedUpdaters.has(key)) {
      let timeoutId = null;
      const wrapped = (...args) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => this.updateQuantity(...args), 300);
      };
      wrapped.cancel = () => clearTimeout(timeoutId);
      this._debouncedUpdaters.set(key, wrapped);
    }
    return this._debouncedUpdaters.get(key);
  }

  connectedCallback() {
    this._boundHandleClick ??= this.handleClick.bind(this);
    this._boundHandleInputChange ??= this.handleInputChange.bind(this);
    this.addEventListener('click', this._boundHandleClick);
    this.addEventListener('change', this._boundHandleInputChange);
    this._itemErrorUnsub = theme.pubsub.subscribe(theme.pubsub.events.CART_ITEM_ERROR, this._handleItemError.bind(this));
    this.applyActiveLineErrors();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._boundHandleClick);
    this.removeEventListener('change', this._boundHandleInputChange);
    this._itemErrorUnsub?.();
  }

  _handleItemError({ key, message }) {
    const lineItem = key ? this.querySelector(`[data-line-item-key="${key}"]`) : null;
    const slot = lineItem?.querySelector('[data-line-error]');
    if (!slot) return;

    slot.textContent = message;
    slot.hidden = false;
  }

  applyActiveLineErrors() {
    theme.cart.errors.eachLine((key, message) => {
      const lineItem = this.querySelector(`[data-line-item-key="${key}"]`);
      const slot = lineItem?.querySelector('[data-line-error]');
      if (!slot) return;
      slot.textContent = message;
      slot.hidden = false;
    });
  }

  _clearLineErrorSlot(lineItem) {
    const slot = lineItem?.querySelector('[data-line-error]');
    if (!slot) return;
    slot.hidden = true;
    slot.textContent = '';
    const key = lineItem.dataset.lineItemKey;
    if (key) theme.cart.errors.clearLine(key);
  }

  handleInputChange(event) {
    const input = event.target.closest('[data-quantity-input]');
    if (!input) return;

    const lineItem = input.closest('[data-line-item-key]');
    if (!lineItem) return;

    const { lineItemKey: key } = lineItem.dataset;
    let quantity = parseInt(input.value, 10);

    if (isNaN(quantity) || quantity < 1) {
      quantity = 1;
      input.value = 1;
    }

    this._clearLineErrorSlot(lineItem);
    this.setLoading(lineItem, true);
    this._getDebouncedUpdate(key)(key, quantity, lineItem);
  }

  getSectionsToRender() {
    const sections = [];

    if (this.cartDrawer?.getSectionsToRender) {
      sections.push(...this.cartDrawer.getSectionsToRender());
    }

    if (this.cartPage) {
      const sectionId = this.cartPage.dataset.sectionId;
      if (sectionId) {
        sections.push({ section: sectionId, selector: 'cart-page' });
      }
    }

    return sections;
  }

  handleClick(event) {
    const target = event.target.closest('[data-action]');
    if (!target) return;

    const { action } = target.dataset;
    const lineItem = target.closest('[data-line-item-key]');
    if (!lineItem) return;

    const { lineItemKey: key } = lineItem.dataset;
    const currentQuantity = this.getCurrentQuantity(lineItem);

    this.processAction(action, key, currentQuantity, lineItem);
  }

  processAction(action, key, currentQuantity, lineItem) {
    const actions = {
      increase: () => this.changeQuantity(key, currentQuantity + 1, lineItem),
      decrease: () => this.handleDecrease(key, currentQuantity, lineItem),
      remove: () => this.removeItem(key, lineItem)
    };

    actions[action]?.();
  }

  handleDecrease(key, currentQuantity, lineItem) {
    this._clearLineErrorSlot(lineItem);
    if (currentQuantity > 1) {
      this.changeQuantity(key, currentQuantity - 1, lineItem);
    } else {
      this.removeItem(key, lineItem);
    }
  }

  getCurrentQuantity(lineItem) {
    const input = lineItem.querySelector('[data-quantity-input]');
    return parseInt(input?.value, 10) || 1;
  }

  changeQuantity(key, quantity, lineItem) {
    this._clearLineErrorSlot(lineItem);
    this.updateQuantityInput(lineItem, quantity);
    this.setLoading(lineItem, true);
    this._getDebouncedUpdate(key)(key, quantity, lineItem);
  }

  updateQuantityInput(lineItem, quantity) {
    const input = lineItem.querySelector('[data-quantity-input]');
    if (input) {
      input.value = quantity;
    }
  }

  async updateQuantity(key, quantity, lineItem) {
    const sections = this.getSectionsToRender();

    try {
      const cart = await theme.cart.change(key, quantity, { sections });
      this.updateCartUI(cart);

      const newCartItems = this.cartPage
        ? this.cartPage.querySelector('cart-items')
        : this.cartDrawer?.querySelector('cart-items') || this;
      const newLineItem = newCartItems.querySelector(`[data-line-item-key="${key}"]`);

      if (newLineItem) {
        this.setLoading(newLineItem, false);
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.setLoading(lineItem, false);
    }
  }

  async removeItem(key, lineItem) {
    this._debouncedUpdaters.get(key)?.cancel();

    const isLastItem = this.querySelectorAll('[data-line-item-key]').length === 1;
    const sections = this.getSectionsToRender();

    theme.cart.errors.clearLine(key);
    this.animateRemoval(lineItem);

    if (isLastItem) {
      this.showContainerLoading(true);
    }

    const snapshot = await this.snapshotLineItem(key);

    try {
      const [cart] = await Promise.all([
        theme.cart.change(key, 0, { sections, ...this.buildUndoOptions(snapshot) }),
        isLastItem ? this.minLoadingDelay(400) : Promise.resolve()
      ]);
      this.showContainerLoading(false);
      this.updateCartUI(cart);
    } catch (error) {
      this.showContainerLoading(false);
      this.revertRemovalAnimation(lineItem);
    }
  }

  async snapshotLineItem(key) {
    try {
      const cart = await theme.cart.fetch();
      return cart.items?.find(item => item.key === key) || null;
    } catch (_) {
      return null;
    }
  }

  buildUndoOptions(item) {
    if (!item) return {};

    return {
      duration: 6000,
      dismissible: false,
      action: {
        label: theme.locales?.cart?.action?.undo,
        onClick: () => theme.cart.add({
          id: item.variant_id,
          quantity: item.quantity,
          properties: item.properties || undefined,
          selling_plan: item.selling_plan_allocation?.selling_plan?.id,
        }, {
          sections: this.getSectionsToRender(),
          openDrawer: this.cartPage ? false : undefined,
        }),
      },
    };
  }

  minLoadingDelay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  animateRemoval(lineItem) {
    lineItem.classList.add('cart-line-item--removing');
  }

  revertRemovalAnimation(lineItem) {
    lineItem.classList.remove('cart-line-item--removing');
  }

  showContainerLoading(isLoading) {
    this.cartDrawer?.setLoading(isLoading);
    this.cartPage?.classList.toggle('cart-page--loading', isLoading);
  }

  updateCartUI(cart) {
    if (this.cartPage) {
      this.updateCartPage(cart);
    } else {
      this.updateCartDrawer(cart);
    }

    theme.pubsub.publish(theme.pubsub.events.CART_UPDATE, {
      cart,
      skipFetch: true,
    });

    const newCartItems = this.cartPage
      ? this.cartPage.querySelector('cart-items')
      : this.cartDrawer?.querySelector('cart-items') || this;
    newCartItems?.applyActiveLineErrors?.();
  }

  updateCartPage(cart) {
    if (!cart.sections) return;

    const sectionId = this.cartPage.dataset.sectionId;
    const sectionHTML = cart.sections[sectionId];

    if (sectionHTML) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = sectionHTML;

      const newContent = tempDiv.querySelector('[data-cart-content]');
      const currentContent = this.cartPage.querySelector('[data-cart-content]');

      if (newContent && currentContent) {
        currentContent.innerHTML = newContent.innerHTML;
      }

      const newCount = tempDiv.querySelector('[data-cart-count]');
      const currentCount = this.cartPage.querySelector('[data-cart-count]');
      if (newCount && currentCount) {
        currentCount.textContent = newCount.textContent;
      }
    }
  }

  updateCartDrawer(cart) {
    if (this.cartDrawer?.renderContents && cart.sections) {
      this.cartDrawer.renderContents(cart);
    }
  }

  setLoading(lineItem, isLoading) {
    lineItem.querySelectorAll('button').forEach((button) => {
      button.disabled = isLoading;
    });

    lineItem.classList.toggle('cart-line-item--loading', isLoading);
  }
}

customElements.define('cart-items', CartItems);
